import React, { useMemo, useState, useCallback } from 'react';
import {
  AccountAuthButton,
  AlertTypes,
  AuthButtonVariantTypes,
  DisclaimerText,
  EmailRegistrationForm,
  EmailRegistrationFormProps,
  OrDivider
} from '@strava/authentication-ui';

import {
  useStravaTranslations,
  useStravaAnalytics,
  useStravaErrorLogging
} from '@strava/container-context';
import { Alert, AlertStatus } from '@strava/ui/Alert';
import { useApple } from '@strava/utils';
import styles from './LohRegistrationPanel.module.scss';

export enum SignupOption {
  apple = 'apple',
  email = 'email',
  facebook = 'facebook',
  google = 'google'
}

export interface LohRegistrationPanelProps extends EmailRegistrationFormProps {
  authorizationUrls: {
    [SignupOption.google]: string;
    [SignupOption.facebook]: string;
  };
  config: {
    appleEnabled: boolean;
    appleState: string;
  };
  flashMessage?: string;
}

const I18N_PREFIX = 'strava.landing_page';

const theme = 'light';

const LohRegistrationPanel = ({
  authorizationUrls,
  config,
  flashMessage,
  reCaptchaSiteKey,
  honeyPot,
  isPremium,
  formAuthToken
}: LohRegistrationPanelProps) => {
  const [alert, setAlert] = useState<AlertTypes>(
    flashMessage ? { severity: AlertStatus.ERROR, message: flashMessage } : null
  );

  const { logError } = useStravaErrorLogging();

  const { t: _t } = useStravaTranslations();
  const { track: _track } = useStravaAnalytics();
  const { signin: appleSignin } = useApple(config.appleState, logError);

  const t = useMemo(
    () => (key: string, params?: object) =>
      _t(`${I18N_PREFIX}.${key}`, { ...params }),
    [_t]
  );

  const track = useCallback(
    (fields) => {
      _track({
        category: 'onboarding',
        page: 'home',
        ...fields,
        properties: {
          ...fields.properties
        }
      });
    },
    [_track]
  );

  const socialAuthConfig = {
    [SignupOption.facebook]: {
      variant: 'facebook',
      enabled: true,
      authUrl: authorizationUrls[SignupOption.facebook]
    },
    [SignupOption.google]: {
      variant: 'google',
      enabled: true,
      authUrl: authorizationUrls[SignupOption.google]
    },
    [SignupOption.apple]: {
      variant: 'apple',
      enabled: config.appleEnabled,
      authUrl: '' // apple uses SDK (useApple hook) to redirect to apple auth screen
    }
  };

  const handleSocialBtnClick = (option: SignupOption, authUrl: string) => {
    track({
      action: 'click',
      element: `${option}_signup`
    });

    if (option === SignupOption.apple) {
      // apple uses SDK (useApple hook) to redirect to apple auth screen
      appleSignin();
    } else {
      window.location.assign(authUrl);
    }
  };

  return (
    <>
      {alert && (
        <Alert
          data-cy="alert"
          hideCloseButton={true}
          alertType={alert.severity}
        >
          {alert.message}
        </Alert>
      )}

      {Object.entries(socialAuthConfig).map(
        ([key, { enabled, variant, authUrl }]) =>
          enabled && (
            <AccountAuthButton
              key={key}
              variant={variant as AuthButtonVariantTypes}
              onClick={() => handleSocialBtnClick(key as SignupOption, authUrl)}
              data-testid={`${key}_auth_btn`}
            >
              {t(`here_to_join.${key}_cta_v2`)}
            </AccountAuthButton>
          )
      )}

      <OrDivider theme={theme} text={t('or_v2')} />

      <EmailRegistrationForm
        authorizationUrl="/join"
        reCaptchaSiteKey={reCaptchaSiteKey}
        honeyPot={honeyPot}
        isPremium={isPremium}
        setAlert={setAlert}
        track={track}
        theme={theme}
        plan="free"
        formAuthToken={formAuthToken}
        idPrefix="loh"
      />

      <DisclaimerText i18nPrefix={I18N_PREFIX} />
      <p className={styles.alreadyAMember}>
        {t(`already_a_member`)}{' '}
        <a
          href="/login"
          onClick={() => {
            track({ action: 'click', element: 'already_member_login' });
          }}
        >
          {t(`log_in`)}
        </a>
      </p>
    </>
  );
};

export default LohRegistrationPanel;
